import React, { useState, useMemo, useRef } from 'react';
import {
  TrendingUpOutlined,
  PeopleOutlined,
  ApiOutlined,
  SearchOutlined,
  CloseOutlined,
  RefreshOutlined,
  PictureAsPdfOutlined,
} from '@mui/icons-material';
import StatCard                from '../../atoms/StatCard/StatCard';
import ExportMenu               from '../../atoms/ExportMenu/ExportMenu';
import DateRangePicker         from '../../molecules/DateRangePicker/DateRangePicker';
import ViewSwitcher            from '../../atoms/ViewSwitcher/ViewSwitcher';
import AdminHierarchyFilter    from '../../atoms/AdminHierarchyFilter/AdminHierarchyFilter';
import ActivityInsightsChart   from '../../molecules/charts/ActivityInsightsChart/ActivityInsightsChart';
import DonutChart              from '../../molecules/charts/DonutChart/DonutChart';
import TopUsersChart           from '../../molecules/charts/TopUsersChart/TopUsersChart';
import UserActivityDetailChart from '../../molecules/charts/UserActivityDetailChart/UserActivityDetailChart';
import { translateEndpoint }   from '../../../utils/logTranslations';
import {
  buildExportName,
  buildFilterLabels,
  exportEchartsPNG,
  exportNodeAsPNG,
  exportRowsAsCSV,
  exportFullObjectsAsCSV,
  generateSecuFireReport,
} from '../../../utils/exportChartsUtils';
import './UserLogs.css';

export const AUTH_COLOR = '#0EA5E9';

const VIEW_MODES = [
  { key: 'daily',   label: 'Jour' },
  { key: 'weekly',  label: 'Semaine' },
  { key: 'monthly', label: 'Mois' },
];

const UserLogs = ({
  data,
  loading,
  startDate,
  endDate,
  onDateChange,
  selectedUserId,
  onUserSelect,
  userDetailData,
  loadingUserDetail,
  admins = [],
  selectedAdminId,
  onAdminSelect,
  onRefresh,
  refreshing = false,
  notifSlot,
  isInternal,
  onIsInternalChange,
}) => {
  const [viewMode,   setViewMode]   = useState('daily');
  const [userSearch, setUserSearch] = useState('');
  const [exportingPDF, setExportingPDF] = useState(false);

  const activityChartRef   = useRef(null);
  const donutChartRef      = useRef(null);
  // Conteneur DOM complet du donut (graphique ECharts + libellé "total"
  // superposé) : nécessaire pour que l'export PNG capture aussi le total
  // au centre, qui n'existe pas dans le canvas ECharts lui-même.
  const donutContainerRef  = useRef(null);

  const selectedUser = useMemo(() =>
    data?.all_users?.find((u) => u.user_id === selectedUserId),
    [data, selectedUserId],
  );

  // Lignes détaillées du profil d'activité : une ligne par appel horodaté.
  // Même logique de rapprochement/filtrage par période que
  // UserActivityDetailChart, pour que l'export corresponde à l'écran.
  const userCallRows = useMemo(() => {
    if (!selectedUserId || !userDetailData) return [];

    const from = startDate ? new Date(startDate) : null;
    const to   = endDate   ? new Date(endDate)   : null;
    if (from) from.setHours(0, 0, 0, 0);
    if (to)   to.setHours(23, 59, 59, 999);

    // timestamps regroupés par endpoint (même clé que le graphique de détail)
    const tsByKey = {};
    (userDetailData.recent_activities || []).forEach((a) => {
      if (!a.timestamp) return;
      const t = new Date(a.timestamp);
      if ((from && t < from) || (to && t > to)) return;
      const key = a.service || a.endpoint || '';
      (tsByKey[key] = tsByKey[key] || []).push(t);
    });

    const rows = [];
    (userDetailData.by_functionality || []).forEach((f) => {
      const full  = f.functionality || '';
      const raw   = f.endpoint || f.name || full;
      const label = translateEndpoint(full || raw, 'auth');
      const total = f.total_usage || f.count || 0;
      const stamps = (tsByKey[raw] || tsByKey[full] || tsByKey[`auth.${raw}`] || [])
        .slice()
        .sort((a, b) => b - a);

      if (!stamps.length) {
        rows.push({ endpoint: label, date: '', appels_endpoint: total });
      } else {
        stamps.forEach((t) => rows.push({
          endpoint: label,
          date: t.toLocaleString('fr-FR'),
          appels_endpoint: total,
        }));
      }
    });
    return rows;
  }, [selectedUserId, userDetailData, startDate, endDate]);

  const { totalActivities, activeUsers, topFunc } = useMemo(() => {
    const rawTopFunc =
      data?.by_functionality?.[0]?.functionality ||
      data?.by_functionality?.[0]?.endpoint ||
      null;
    return {
      totalActivities: data?.summary?.total_activities ?? 0,
      activeUsers:     data?.summary?.active_users     ?? 0,
      topFunc: rawTopFunc ? translateEndpoint(rawTopFunc, 'auth') : 'N/A',
    };
  }, [data]);

  const donutEndpoints = useMemo(() =>
    (data?.by_functionality || [])
      .map((f) => {
        const raw = f.functionality || f.endpoint || '';
        return {
          endpoint:    translateEndpoint(raw, 'auth'),
          total_usage: f.total_usage || f.count || 0,
        };
      })
      .filter((e) => e.total_usage > 0),
    [data],
  );

  const adaptedMicroservices = useMemo(() => {
    const fromApi = (data?.by_microservice || []).map((ms) => ({ ...ms, color: AUTH_COLOR }));
    if (fromApi.length > 0) return fromApi;
    return [{
      microservice: 'auth',
      total_usage:  data?.summary?.total_activities ?? totalActivities,
      color:        AUTH_COLOR,
    }];
  }, [data, totalActivities]);

  const selectedAdminName = admins.find((a) => a.id === selectedAdminId)?.username || null;

  // Filtres actifs à répercuter dans le nommage de TOUS les exports.
  const activeFilters = useMemo(() => [
    selectedAdminName ? `hierarchie-${selectedAdminName}` : null,
    isInternal === true ? 'comptes-internes' : isInternal === false ? 'comptes-externes' : null,
  ], [selectedAdminName, isInternal]);

  // Libellés des filtres actifs : utilisés dans l'en-tête du PDF ET dans le
  // bandeau des captures PNG.
  const filterLabels = useMemo(
    () => buildFilterLabels({ adminName: selectedAdminName, isInternal, startDate, endDate }),
    [selectedAdminName, isInternal, startDate, endDate],
  );

  // En-tête des PNG : titre de la section + page + filtres actifs
  const pngMeta = (title) => ({
    title,
    subtitle: 'Logs d’authentification · Microservice Auth',
    filters: filterLabels,
  });

  // ── Handlers d'export ────────────────────────────────────────────

  // Évolution temporelle
  const handleExportTrendPNG = () => exportEchartsPNG(
    activityChartRef,
    buildExportName({ section: 'Evolution temporelle', startDate, endDate, filters: activeFilters }),
    pngMeta('Évolution temporelle'),
  );
  const handleExportTrendCSV = () => exportRowsAsCSV(
    (data?.daily_trend || []).map((d) => ({
      date: d.date || d.day || '',
      total_activites: d.total_activities ?? d.count ?? 0,
    })),
    buildExportName({ section: 'Evolution temporelle', startDate, endDate, filters: activeFilters }),
  );

  // Répartition des endpoints (donut) — capture du conteneur DOM complet
  // pour que le total affiché au centre soit bien inclus dans le PNG.
  const handleExportDonutPNG = () => exportNodeAsPNG(
    donutContainerRef.current,
    buildExportName({ section: 'Repartition des endpoints', startDate, endDate, filters: activeFilters }),
    pngMeta('Répartition des endpoints'),
  );
  const handleExportDonutCSV = () => exportRowsAsCSV(
    donutEndpoints.map((e) => ({ endpoint: e.endpoint, appels: e.total_usage })),
    buildExportName({ section: 'Repartition des endpoints', startDate, endDate, filters: activeFilters }),
  );

  // Classement des utilisateurs — plus d'export PNG ; le CSV reprend
  // désormais TOUTES les informations disponibles pour chaque utilisateur.
  const handleExportTopUsersCSV = () => exportFullObjectsAsCSV(
    data?.all_users || [],
    buildExportName({ section: 'Classement des utilisateurs', startDate, endDate, filters: activeFilters }),
  );

  // Profil d'activité — une ligne par appel (endpoint + date + total de
  // l'endpoint). Nommage avec le nom de l'utilisateur concerné.
  const handleExportUserDetailCSV = () => exportRowsAsCSV(
    userCallRows,
    buildExportName({
      section: "Profil d'activite",
      subject: selectedUser?.username || (selectedUserId ? `user-${selectedUserId}` : null),
      startDate,
      endDate,
      filters: activeFilters,
    }),
  );

  // Export PDF global : un vrai rapport de traçabilité (synthèse + tableaux
  // complets), pas une capture d'écran de la page.
  const handleExportPDF = async () => {
    if (exportingPDF) return;
    setExportingPDF(true);
    try {
      await generateSecuFireReport({
        fileName: buildExportName({
          section: 'rapport-logs-authentification',
          startDate,
          endDate,
          filters: activeFilters,
        }),
        title: "Rapport de traçabilité — Logs d'authentification",
        subtitle: 'SecuFire · Microservice Auth',
        filtersLine: filterLabels.join('   ·   '),
        summary: {
          totalActivities,
          activeUsers,
          totalUsers: data?.summary?.total_users,
          topFunctionality: topFunc,
        },
        dailyTrend: (data?.daily_trend || []).map((d) => ({
          date: d.date || d.day || '',
          total_activities: d.total_activities ?? d.count ?? 0,
        })),
        endpointDistribution: donutEndpoints,
        users: data?.all_users || [],
        selectedUser: selectedUser || null,
        selectedUserEndpoints: (userDetailData?.by_functionality || []).map((f) => ({
          endpoint: translateEndpoint(f.functionality || f.endpoint || '', 'auth'),
          total_usage: f.total_usage || f.count || 0,
        })),
        // Historique horodaté des appels de l'utilisateur sélectionné.
        extraSections: selectedUser ? [{
          title: `Historique des appels — ${selectedUser.username || `User ${selectedUser.user_id}`}`,
          table: userCallRows.some((r) => r.date) ? {
            columns: [
              { key: 'date', label: 'Date', width: 1 },
              { key: 'endpoint', label: 'Endpoint', width: 2 },
            ],
            rows: userCallRows.filter((r) => r.date),
          } : undefined,
          paragraph: userCallRows.some((r) => r.date)
            ? undefined
            : 'Aucun appel horodaté pour cette période.',
        }] : [],
      });
    } finally {
      setExportingPDF(false);
    }
  };

  if (loading && !refreshing) {
    return (
      <div className="ul-loading">
        <div className="ul-spinner" />
        <p>Chargement des logs utilisateurs …</p>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="ul-wrapper">

      {/* ── HEADER ── */}
      <div className="ul-header">

        {/* Bloc KPIs — cartes compactes (small) pour libérer de la
            place horizontale et laisser les filtres tenir sur une
            seule ligne (zoom navigateur, largeurs intermédiaires). */}
        <div className="ul-stats-block">
          <div className="ul-kpis-row">
            <div className="ul-kpi-item">
              <StatCard icon={<TrendingUpOutlined />} label="Total Activités" value={totalActivities.toLocaleString()} variant="red" small />
            </div>
            <div className="ul-kpi-item ul-kpi-item--full">
              <StatCard icon={<ApiOutlined />} label="Top fonctionnalité" value={topFunc} variant="orange" small />
            </div>
          </div>
        </div>

        <div className="header-sep" />

        {/* Bloc filtres */}
        <div className="ul-filters-block">
          <div className="ul-filter-group ul-filter-group--admin">
            <span className="ul-filter-label">Admin :</span>
            <AdminHierarchyFilter
              admins={admins}
              selectedAdminId={selectedAdminId}
              onSelect={onAdminSelect}
            />
          </div>

          <div className="ul-filter-group ul-filter-group--comptes">
            <span className="ul-filter-label">Comptes :</span>
            <select
              value={isInternal === null ? 'all' : isInternal ? 'internal' : 'external'}
              onChange={(e) => {
                const v = e.target.value;
                onIsInternalChange(v === 'all' ? null : v === 'internal' ? true : false);
              }}
              className="ul-internal-select"
            >
              <option value="all">Tous</option>
              <option value="external">Externes</option>
              <option value="internal">Internes</option>
            </select>
          </div>

          <div className="ul-filter-group ul-filter-group--date">
            <span className="ul-filter-label">Période :</span>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartChange={(date) => onDateChange(date, endDate)}
              onEndChange={(date)   => onDateChange(startDate, date)}
            />
          </div>
        </div>

        <div className="header-sep" />

        {/* Bloc actions : export PDF + refresh + notification.
            Le bouton Rafraîchir ne garde que l'icône (plus de texte)
            pour libérer de la place à côté des filtres. */}
        <div className="ul-actions-slot">
          <button
            className={`ul-pdf-btn${exportingPDF ? ' ul-pdf-btn--loading' : ''}`}
            onClick={handleExportPDF}
            disabled={exportingPDF}
            title="Exporter le rapport de traçabilité en PDF"
          >
            {exportingPDF
              ? <span className="ul-pdf-spinner" />
              : <PictureAsPdfOutlined style={{ fontSize: 14 }} />}
            <span>{exportingPDF ? 'Génération…' : 'PDF'}</span>
          </button>
          <button
            className={`ul-refresh-btn${refreshing ? ' ul-refresh-btn--loading' : ''}`}
            onClick={onRefresh}
            disabled={refreshing}
            title="Rafraîchir"
          >
            <RefreshOutlined style={{ fontSize: 15 }} />
          </button>
          {notifSlot}
        </div>

      </div>

      {/* Admin badge */}
      {selectedAdminId && selectedAdminName && (
        <div className="ul-admin-badge">
          <span className="ul-admin-badge-dot" />
          <span className="ul-admin-badge-text">
            Données filtrées · hiérarchie de <strong>{selectedAdminName}</strong>
          </span>
        </div>
      )}

      {/* is_internal badge */}
      {isInternal !== null && (
        <div className="ul-admin-badge" style={{ background: isInternal ? '#f0fdf4' : '#eff6ff', borderColor: isInternal ? '#86efac' : '#93c5fd' }}>
          <span className="ul-admin-badge-dot" style={{ background: isInternal ? '#16a34a' : '#2563eb' }} />
          <span className="ul-admin-badge-text">
            Filtre actif · comptes <strong style={{ color: isInternal ? '#16a34a' : '#2563eb' }}>{isInternal ? 'internes' : 'externes'}</strong> uniquement
          </span>
        </div>
      )}

      {/* Tendance d'utilisation */}
      <div className="ul-section-label">Tendance d'utilisation</div>
      <div className="ul-top-row">
        <div className="ul-chart-card ul-top-card">
          <div className="ul-chart-header--row">
            <div className="ul-chart-header-left">
              <h3 className="ul-chart-title">Évolution temporelle</h3>
              <span className="ul-chart-subtitle">Microservice d'authentification</span>
              <div className="ul-chart-switcher-row">
                <ViewSwitcher
                  modes={VIEW_MODES}
                  value={viewMode}
                  onChange={setViewMode}
                  color={AUTH_COLOR}
                />
              </div>
            </div>
            <ExportMenu
              color={AUTH_COLOR}
              onExportPNG={handleExportTrendPNG}
              onExportCSV={handleExportTrendCSV}
            />
          </div>
          <div className="ul-chart-body">
            <ActivityInsightsChart
              ref={activityChartRef}
              dailyTrend={(data.daily_trend || []).map(d => {
                const rawDate = d.date || d.day || d.timestamp?.slice(0, 10) || '';
                const [y, mo, day] = rawDate.split('-').map(Number);
                const localDate = y
                  ? `${y}-${String(mo).padStart(2,'0')}-${String(day).padStart(2,'0')}`
                  : rawDate;
                return {
                  ...d,
                  date: localDate,
                  total_activities: d.total_activities ?? d.count ?? d.total ?? 0,
                };
              })}
              microservices={adaptedMicroservices}
              startDate={startDate}
              endDate={endDate}
              viewMode={viewMode}
              onViewChange={setViewMode}
            />
          </div>
        </div>

        <div className="ul-chart-card ul-top-card">
          <div className="ul-chart-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div className="ul-chart-header-left">
              <h3 className="ul-chart-title">Répartition des endpoints</h3>
              <span className="ul-chart-subtitle">
                Répartition par endpoint dans le microservice d'authentification
              </span>
            </div>
            <ExportMenu
              color={AUTH_COLOR}
              onExportPNG={handleExportDonutPNG}
              onExportCSV={handleExportDonutCSV}
            />
          </div>
          <div className="ul-chart-body ul-donut-body" ref={donutContainerRef}>
            <DonutChart
              ref={donutChartRef}
              mode="endpoint"
              endpoints={donutEndpoints}
              totalCalls={totalActivities}
              color={AUTH_COLOR}
            />
          </div>
        </div>
      </div>

      {/* Détail utilisateurs */}
      <div className="ul-section-label">Détail utilisateurs</div>
      <div className="ul-bottom-row">
        <div className="ul-chart-card">
          <div className="ul-chart-header ul-chart-header--search">
            <div className="ul-chart-header-left">
              <h3 className="ul-chart-title">Classement des utilisateurs</h3>
              <span className="ul-chart-subtitle">
                {data.summary?.total_users} membres · cliquez pour les détails
              </span>
            </div>
            <div className="ul-header-actions" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* Plus d'export PNG ici : uniquement le CSV, qui contient
                  désormais toutes les informations de chaque utilisateur. */}
              <ExportMenu
                color={AUTH_COLOR}
                onExportCSV={handleExportTopUsersCSV}
              />
              <div className={`ul-user-search${userSearch ? ' ul-user-search--active' : ''}`}>
                <SearchOutlined className="ul-user-search__icon" />
                <input
                  type="text"
                  className="ul-user-search__input"
                  placeholder="Nom, email ou organisation…"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                />
                {userSearch && (
                  <button
                    className="ul-user-search__clear"
                    onClick={() => setUserSearch('')}
                    title="Effacer"
                  >
                    <CloseOutlined style={{ fontSize: 13 }} />
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="ul-chart-body ul-chart-body--scroll">
            <TopUsersChart
              users={data.all_users}
              totalActivities={totalActivities}
              onUserSelect={onUserSelect}
              selectedUserId={selectedUserId}
              searchQuery={userSearch}
            />
          </div>
        </div>

        <div className="ul-chart-card">
          <div className="ul-chart-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div className="ul-chart-header-left">
              <h3 className="ul-chart-title">Profil d'activité</h3>
              {selectedUser && (
                <span className="ul-chart-subtitle">
                  {selectedUser.username || `User ${selectedUser.user_id}`}
                </span>
              )}
            </div>
            {/* Plus d'export PNG ici non plus : seul le CSV reste proposé. */}
            <ExportMenu
              color={AUTH_COLOR}
              onExportCSV={handleExportUserDetailCSV}
              disabled={!selectedUserId}
            />
          </div>
          <div className="ul-chart-body ul-chart-body--scroll">
            <UserActivityDetailChart
              userId={selectedUserId}
              username={selectedUser?.username || `User ${selectedUserId}`}
              activityData={userDetailData}
              loading={loadingUserDetail}
              startDate={startDate}
              endDate={endDate}
            />
          </div>
        </div>
      </div>

    </div>
  );
};

export default UserLogs;